import { Fragment, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { SystemIssueDetail } from "@backstory/protocol";
import { ErrorState, Skeleton } from "../../components/States";
import { CopyButton, PageHeader } from "../../components/ui";
import { AiBlockedTooltip, AiSparkle } from "../ai/components/common";
import { usePlatformAiCapability } from "./platformAi";
import "../ai/ai.css";
import "./staff.css";
import { formatDateTime, formatRelative } from "../../lib/format";
import { staffApi, staffKeys } from "./api";
import { AcknowledgeDialog, InvestigationPanel, SeverityBadge, sourceLabels, tenantLabel, useSystemIssueActions } from "./systemIssues";

/**
 * One grouped platform failure. Everything about the problem lives here: how
 * often and how widely it happens, the diagnostic behind the most recent
 * occurrence, the saved AI investigation, and the individual failures to chase.
 */
export function StaffSystemIssuePage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const issue = useQuery({
    queryKey: staffKeys.systemIssue(id),
    queryFn: () => staffApi.systemIssue(id),
    refetchInterval: (query) => (query.state.data?.investigation?.state === "pending" ? 1_500 : false),
  });

  const back = (
    <Link to="/staff/system" className="btn ghost">
      ← System issues
    </Link>
  );
  if (issue.isPending) {
    return (
      <div>
        <PageHeader back={back} title="Loading issue…" />
        <Skeleton lines={8} />
      </div>
    );
  }
  if (issue.isError) {
    return (
      <div>
        <PageHeader back={back} title="System issue" />
        <ErrorState error={issue.error} retry={() => void issue.refetch()} />
      </div>
    );
  }
  return <IssueDetail issue={issue.data} back={back} />;
}

function IssueDetail({ issue, back }: { issue: SystemIssueDetail; back: ReactNode }) {
  const ai = usePlatformAiCapability();
  const { investigate, reopen } = useSystemIssueActions(issue.id);
  const [acknowledging, setAcknowledging] = useState(false);
  const pending = issue.investigation?.state === "pending" || investigate.isPending;
  const failed = issue.investigation?.state === "failed" ? issue.investigation.error : "";
  const investigated = !!issue.investigation?.result;

  return (
    <div className="sys-issue-page">
      <AcknowledgeDialog issue={issue} open={acknowledging} onClose={() => setAcknowledging(false)} />
      <PageHeader
        back={back}
        title={issue.title}
        subtitle={
          <>
            <SeverityBadge severity={issue.severity} /> <span className="muted">{sourceLabels[issue.source]}</span>
            {issue.status === "acknowledged" && (
              <>
                {" · "}
                <span className="muted">
                  Acknowledged {issue.acknowledgedAt ? formatRelative(issue.acknowledgedAt) : ""}
                  {issue.acknowledgedBy ? ` by ${issue.acknowledgedBy}` : ""}
                </span>
              </>
            )}
          </>
        }
        actions={
          <>
            {issue.status === "acknowledged" ? (
              <button className="btn" type="button" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
                Reopen
              </button>
            ) : (
              <button className="btn" type="button" onClick={() => setAcknowledging(true)}>
                Acknowledge…
              </button>
            )}
            <AiBlockedTooltip ai={ai}>
              <button
                type="button"
                className="btn primary ai-btn"
                disabled={ai.blocked || pending}
                onClick={() => investigate.mutate()}
                title={ai.titleFor(investigated ? "Investigate this issue again" : "Investigate this issue with AI")}
              >
                <AiSparkle size={12} />
                {pending ? "Investigating…" : investigated ? "Re-investigate" : "Investigate"}
              </button>
            </AiBlockedTooltip>
          </>
        }
      />

      <div className="sys-stats">
        <Stat label="Failures" value={issue.occurrences.toLocaleString()} />
        <Stat label="First seen" value={formatRelative(issue.firstSeen)} title={formatDateTime(issue.firstSeen)} />
        <Stat label="Last seen" value={formatRelative(issue.lastSeen)} title={formatDateTime(issue.lastSeen)} />
        <Stat label="Tenant" value={tenantLabel(issue)} text />
      </div>

      {(pending || failed) && (
        <div className="card section sys-issue-note" role="status">
          {pending ? "Investigating this issue. It runs in the background, so you can leave this page." : failed}
        </div>
      )}
      {investigated && <InvestigationPanel issue={issue} pending={pending} />}

      <section className="card section">
        <div className="sys-section-head">
          <h3>Latest diagnostic</h3>
          <CopyButton value={issue.detail} label="Copy" />
        </div>
        {/* Facts that qualify the diagnostic sit with it rather than in a card
            of their own, where three short rows read as an empty panel. */}
        <div className="sys-facts">
          <Fact label="Subject">
            {issue.subjectType} <span className="mono">{issue.subjectId}</span>
          </Fact>
          {issue.projectName && <Fact label="Project">{issue.projectName}</Fact>}
          {issue.environmentCount > 0 && (
            <Fact label="Environment">
              {issue.environmentCount > 1 ? `${issue.environmentCount} environments` : issue.environment}
            </Fact>
          )}
          {!!issue.attempts && <Fact label="Most attempts">{issue.attempts}</Fact>}
          {(issue.endUserName || issue.endUserId) && <Fact label="Affected user">{issue.endUserName || issue.endUserId}</Fact>}
        </div>
        <Diagnostic detail={issue.detail} />
      </section>

      <section className="card section">
        <h3>Recent failures</h3>
        {issue.recentOccurrences.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            The individual failures behind this issue have aged out of the source tables.
          </p>
        ) : (
          <OccurrenceTable issue={issue} />
        )}
      </section>
    </div>
  );
}

/**
 * The failures behind a group. Columns that no occurrence can fill are left out
 * rather than printed as a column of dashes: which context exists depends on the
 * source, and a background job knows nothing about an end user.
 */
function OccurrenceTable({ issue }: { issue: SystemIssueDetail }) {
  const rows = issue.recentOccurrences;
  const showTenant = issue.orgCount > 1;
  const showEnvironment = rows.some((o) => o.environment);
  const showUser = rows.some((o) => o.endUserName || o.endUserId);
  return (
    <div className="table-scroll">
      <table className="table" data-testid="system-issue-occurrences">
        <thead>
          <tr>
            <th>When</th>
            <th>Subject</th>
            {showTenant && <th>Tenant</th>}
            {showEnvironment && <th>Env</th>}
            {showUser && <th>User</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="static">
              <td className="small" title={formatDateTime(o.occurredAt)}>
                {formatRelative(o.occurredAt)}
              </td>
              <td>
                <span className="muted small">{o.subjectType}</span> <span className="mono small">{o.subjectId}</span>
                {!!o.attempts && <span className="muted small"> · {o.attempts} attempts</span>}
              </td>
              {showTenant && (
                <td className="small">
                  {o.orgName || o.orgId || <span className="muted">Platform</span>}
                  {o.projectName && <span className="muted small" style={{ display: "block" }}>{o.projectName}</span>}
                </td>
              )}
              {showEnvironment && <td>{o.environment ? <span className="badge neutral">{o.environment}</span> : <span className="muted">—</span>}</td>}
              {showUser && <td className="small">{o.endUserName || o.endUserId || <span className="muted">—</span>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Worker diagnostics usually carry a provider's JSON error response inline, on
 * one line, behind a sentence of our own. That payload is the most useful part of
 * the failure and the least readable, so it is split off and printed as
 * structure. Anything that does not parse is shown untouched, because a
 * diagnostic reformatted into something else is worse than a long one.
 */
function splitDiagnostic(detail: string): { prefix: string; json?: unknown } {
  const start = detail.indexOf("{");
  if (start < 0) return { prefix: detail };
  try {
    const json: unknown = JSON.parse(detail.slice(start));
    if (typeof json !== "object" || json === null) return { prefix: detail };
    return { prefix: detail.slice(0, start).trimEnd(), json };
  } catch {
    return { prefix: detail };
  }
}

function Diagnostic({ detail }: { detail: string }) {
  const { prefix, json } = splitDiagnostic(detail);
  const structured = json !== undefined;
  return (
    <pre className="mono small sys-issue-detail" data-testid="system-issue-diagnostic">
      {prefix}
      {structured && prefix ? "\n" : null}
      {structured && <JsonValue value={json} depth={0} />}
    </pre>
  );
}

/**
 * Colours each kind of token so a long payload can be scanned for the one field
 * that matters. Rendered from the parsed value rather than by tokenising the
 * text, so the colours cannot disagree with the structure: a brace inside a
 * message stays part of the string.
 */
function JsonValue({ value, depth }: { value: unknown; depth: number }) {
  if (value === null) return <span className="lit">null</span>;
  if (typeof value === "boolean") return <span className="lit">{String(value)}</span>;
  if (typeof value === "number") return <span className="num">{String(value)}</span>;
  if (typeof value === "string") return <span className="str">{JSON.stringify(value)}</span>;

  const pad = "  ".repeat(depth);
  const inner = "  ".repeat(depth + 1);
  const entries: Array<[string | null, unknown]> = Array.isArray(value)
    ? value.map((item) => [null, item] as [null, unknown])
    : Object.entries(value as Record<string, unknown>);
  const [open, close] = Array.isArray(value) ? ["[", "]"] : ["{", "}"];
  if (entries.length === 0) return <span className="punc">{open + close}</span>;
  return (
    <>
      <span className="punc">{open}</span>
      {"\n"}
      {entries.map(([key, item], i) => (
        <Fragment key={key ?? i}>
          {inner}
          {key !== null && (
            <>
              <span className="key">{JSON.stringify(key)}</span>
              <span className="punc">: </span>
            </>
          )}
          <JsonValue value={item} depth={depth + 1} />
          {i < entries.length - 1 && <span className="punc">,</span>}
          {"\n"}
        </Fragment>
      ))}
      {pad}
      <span className="punc">{close}</span>
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="sys-fact">
      <span className="label">{label}</span>
      <span>{children}</span>
    </div>
  );
}

/** `text` for values that are words rather than figures, which need the room. */
function Stat({ label, value, title, text }: { label: string; value: ReactNode; title?: string; text?: boolean }) {
  return (
    <div className="card sys-stat" title={title}>
      <span className="label">{label}</span>
      <span className={`value${text ? " text" : ""}`}>{value}</span>
    </div>
  );
}
